import React, { useState, useEffect } from 'react';
import { useMessage } from './';
import { customPropertyService, customPropertyValueService } from '../services';
import { renderUrl, isLinkableUrl, getBatchEditValue } from '../helpers';
import {
  ObjectDetail,
  TextareaReadOnly,
  TextareaCode,
  textareaInitialState,
  textareaCodeInitialState,
  textareaToRaw,
  editorStateToPlainText,
  PrincipalLink,
} from '../components';
import { useTooltip } from '../hooks';

export const useCustomProperties = ({ objectName, objectIds }) => {
  const { showMessage } = useMessage();

  const [customProperties, setCustomProperties] = useState([]);
  const [customPropertyValues, setCustomPropertyValues] = useState([]);
  const [formCustomPropertyValues, _setFormCustomPropertyValues] = useState({});
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState({});
  const [loadingCustomPropertyValues, setLoadingCustomPropertyValues] = useState(false);

  const { showTooltip, hideTooltip } = useTooltip();

  useEffect(() => {
    fetchCustomProperties();
  }, []);

  useEffect(() => {
    objectIds.length > 1 && setBatchEdit(true);
  }, []);

  const fetchCustomProperties = async (
    _fetchCustomProperties = true,
    _fetchCustomPropertyValues = true
  ) => {
    // objectName is undefined if the object does not have a custom property object map
    if (objectName) {
      try {
        setLoadingCustomPropertyValues(true);
        let promises = [];

        // Fetch custom properties
        if (_fetchCustomProperties) {
          promises.push(
            customPropertyService.getByObjectName(objectName).catch(e => {
              throw e;
            })
          );
        } else {
          promises.push(Promise.resolve());
        }

        // Fetch custom property values
        // objectIds is empty array when creating a new asset
        if (_fetchCustomPropertyValues && objectIds.length > 0) {
          promises.push(
            customPropertyValueService
              .getByObjectId(objectName, objectIds.filter(x => x).join(',') || -1)
              .catch(e => {
                throw e;
              })
          );
        }

        await Promise.all(promises).then(res => {
          if (res[0]) {
            // Set custom properties
            setCustomProperties(res[0].custom_properties);
          }

          // Set custom property values
          if (res[1] && res[1].custom_property_values.length > 0) {
            // Set raw custom property values
            setCustomPropertyValues(res[1].custom_property_values);

            // Format for form usage
            let tmpFormCustomPropertyValues = {};

            res[1].custom_property_values.forEach(item => {
              let customPropertyTypeCode = item.custom_property_type_code;
              let value = item.custom_property_value;
              let tmp = {};

              tmp[item.custom_property_code] = {
                value: value,
                custom_property_id: item.custom_property_id,
                custom_property_type_code: customPropertyTypeCode,
              };

              // Create a separate entry for secondary values
              if (item.custom_property_secondary_value) {
                tmp[item.custom_property_code + '__secondary_value'] = {
                  value: item.custom_property_secondary_value,
                  custom_property_id: item.custom_property_id,
                  custom_property_type_code: customPropertyTypeCode,
                };
              }

              tmpFormCustomPropertyValues[item.object_id] = {
                ...(tmpFormCustomPropertyValues[item.object_id] || {}),
                ...tmp,
              };
            });

            // Batch edit
            if (objectIds.length > 1) {
              let tmp = Object.values(tmpFormCustomPropertyValues);

              // Build array for batch edit value lookup
              let tmpValues = tmp.map(x => {
                return Object.entries(x).reduce((obj, item) => {
                  const [key, value] = item;
                  obj[key] = value.value;
                  return obj;
                }, {});
              });

              let tmpBatchCustomPropertyValues = {};

              // Aggregate values to build the form data
              tmp.forEach(item =>
                Object.entries(item).forEach(([key, value]) => {
                  tmpBatchCustomPropertyValues[key] = {
                    ...value,
                    value: getBatchEditValue(tmpValues, key),
                  };
                })
              );

              tmpFormCustomPropertyValues = tmpBatchCustomPropertyValues;

              // Single edit
            } else {
              tmpFormCustomPropertyValues = Object.values(tmpFormCustomPropertyValues)[0];
            }

            // Format values for
            Object.entries(tmpFormCustomPropertyValues).forEach(([key, object]) => {
              let value = object.value;
              switch (object.custom_property_type_code) {
                case 'boolean':
                  value = value === 'true' ? true : value === 'false' ? false : value;
                  break;
                case 'code':
                  value = textareaCodeInitialState({ value: value });
                  break;
                case 'description':
                  value = textareaInitialState({ value: value });
                  break;
                default:
                // Nothing
              }

              tmpFormCustomPropertyValues[key] = {
                ...tmpFormCustomPropertyValues[key],
                value: value,
              };
            });

            _setFormCustomPropertyValues(tmpFormCustomPropertyValues);
          }
        });
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        setLoadingCustomPropertyValues(false);
      }
    }
  };

  //const setFormCustomPropertyValues = ({name, value, event}) => {
  const setFormCustomPropertyValues = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        _setFormCustomPropertyValues(prev => ({
          ...prev,
          [name]: {
            value,
            custom_property_type_code: event.customPropertyTypeCode,
            custom_property_id: event.customPropertyId,
          },
        }));
    }
  };

  const getCustomPropertyValuesForSubmit = itemId => {
    // Format custom properties for submit
    let tmpCustomPropertyValues = {};

    Object.entries(formCustomPropertyValues).forEach(([key, value]) => {
      let val = value.value;

      switch (value.custom_property_type_code) {
        case 'code':
          val = editorStateToPlainText(val);
          break;
        case 'description':
          val = textareaToRaw({ value: val });
          break;
        default:
        //nothing
      }

      if (!key.includes('__secondary_value')) {
        tmpCustomPropertyValues[key] = {
          value: val,
          custom_property_id: value.custom_property_id,
          secondary_value:
            value.secondary_value || formCustomPropertyValues[key + '__secondary_value']?.value,
        };
      }
    });

    // Batch edit
    if (batchEdit) {
      let tmp = {};

      // Find original values
      customPropertyValues
        .filter(x => x.object_id === itemId)
        .forEach(
          x =>
            (tmp[x.custom_property_code] = {
              value: x.custom_property_value,
              custom_property_id: x.custom_property_id,
              secondary_value: x.custom_property_secondary_value,
            })
        );

      // Append changed values
      Object.entries(batchEditItems).forEach(([key, value]) => {
        key = key.replace('batch_', '');

        // Only overwrite properties that are selected for batch edit, i.e. value = true.
        // Only overwrite properties if values have changed, i.e. is not "multiple"
        if (value) {
          tmp[key] = {
            value:
              tmpCustomPropertyValues[key].value === 'multiple'
                ? tmp[key].value
                : tmpCustomPropertyValues[key].value,
            custom_property_id: tmpCustomPropertyValues[key].custom_property_id,
            secondary_value:
              tmpCustomPropertyValues[key].secondary_value === 'multiple'
                ? tmp[key].secondary_value
                : tmpCustomPropertyValues[key].secondary_value,
          };
        }
      });

      tmpCustomPropertyValues = tmp;
    }

    return { customPropertyValues: tmpCustomPropertyValues };
  };

  const renderCustomPropertyValues = () => {
    let main = [];
    let details = [];

    if (customPropertyValues.length > 0) {
      customPropertyValues.forEach((item, index) => {
        let customPropertyTypeCode = item.custom_property_type_code;
        let value = item.custom_property_value;
        let data = value;
        switch (customPropertyTypeCode) {
          case 'boolean':
            data = value === 'true' ? 'Yes' : value === 'false' ? 'No' : null;
            break;
          case 'list':
            data = (
              <span
                onMouseEnter={() =>
                  showTooltip({
                    title: item.custom_property_display_value,
                    subTitle: item.custom_property_display_secondary_value,
                    bodyText: item.custom_property_display_description,
                  })
                }
                onMouseLeave={hideTooltip}
              >
                {item.custom_property_display_value}
              </span>
            );
            break;
          case 'code':
            data = (
              <TextareaCode
                name="code-editor"
                value={textareaCodeInitialState({ value: value })}
                readOnly={true}
                showLineNumbers={false}
              />
            );

            break;
          case 'description':
            data = (
              <TextareaReadOnly value={textareaInitialState({ value: value, readOnly: true })} />
            );
            break;
          case 'url':
            // Only a http(s) (or scheme-less) URL becomes a link; a path or another scheme is shown
            // as text, matching how asset URLs are rendered (ObjectDetails + isLinkableUrl).
            data = isLinkableUrl(value) ? (
              <a
                href={renderUrl(value)}
                className={'link' + (item.custom_property_secondary_value ? ' external-link' : '')}
                target="_blank"
                rel="noreferrer"
              >
                <span>{item.custom_property_secondary_value || value}</span>
              </a>
            ) : (
              <span>{item.custom_property_secondary_value || value}</span>
            );
            break;
          case 'user':
            data = <PrincipalLink principalId={value} />;
            break;
          default:
          // Nothing
        }

        if (item.custom_property_level_code === 'main') {
          main.push(
            <ObjectDetail
              key={index}
              label={item.custom_property_name}
              data={data}
              tooltip={item.custom_property_description}
            />
          );
        } else {
          details.push({
            title: item.custom_property_name,
            data: data,
            tooltip: item.custom_property_description,
          });
        }
      });
    }

    return { details, main };
  };

  const resetFormCustomPropertyValues = () => {
    _setFormCustomPropertyValues({});
    setBatchEdit(false);
  };

  const hasCustomProperties = () => {
    return customProperties.length > 0;
  };

  return {
    customProperties,
    fetchCustomProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues: batchEdit,
    batchEditItemsFormCustomPropertyValues: batchEditItems,
    setFormCustomPropertyValues,
    hasCustomProperties: hasCustomProperties(),
    renderCustomPropertyValues,
    resetFormCustomPropertyValues,
    getCustomPropertyValuesForSubmit,
  };
};
